import {
  CAMPAIGN_KEYS,
  CLICK_KEYS,
  captureTouch,
  hasCampaign,
  mergeTouch,
  readStoredTouch,
  removeStoredTouch,
  writeStoredTouch,
  type Touch,
} from './attribution';
import type { AnalyticsConfig } from './config';
import {
  allowsTags,
  DENIED_CONSENT,
  googleConsentFor,
  sameDecision,
  type ConsentDecision,
} from './consent';
import { loadCookiebot, onCookiebotEvents, readCookiebotDecision } from './cookiebot';
import { gtag, loadGtm, pushDataLayer } from './dataLayer';
import { readClientId } from './gaCookies';
import { isLocationClean, sanitizePath, sanitizeReferrer, sanitizeUrl } from './sanitize';

// The consent-gated GA4 runtime (#1458). One instance per page. GTM is
// injected only once ALL of these hold, re-checked on every change:
//   1. Cookiebot (or the dev override) reports an answered statistics or marketing grant;
//   2. the URL is clean (no fragment, allowlisted query, no token segments), so
//      the Google tag never starts on a URL carrying a token or an email;
//   3. in the recruiter console, /auth/me said the organisation is eligible.
// Nothing is queued before that: events fired while the gate is closed are
// dropped, because the dataLayer would replay them once GTM loaded.
// GA4 events and identity additionally require statistics consent, even when
// marketing consent has already loaded the container.

export type Eligibility = 'unknown' | 'eligible' | 'ineligible';

/** The client-side events and their only allowed parameters. */
export interface AnalyticsEvents {
  account_type_selected: { account_type: 'employer' | 'candidate' };
  company_selected: { org_type: 'employer' | 'charity'; flow: 'pre_auth' | 'post_auth' };
  account_create_click: Record<string, never>;
  profile_details_submitted: Record<string, never>;
  job_tab_viewed: { tab: 'overview' | 'matches' | 'pipeline' };
}

export type AnalyticsEventName = keyof AnalyticsEvents;

export interface Identity {
  userId: string;
  orgRef: string;
}

/**
 * One Cookiebot decision as auth accepts it, both inside the signup
 * attribution and on PUT /auth/me/analytics-consent.
 */
export interface ConsentPayload {
  source: 'cookiebot';
  consent_id?: string;
  statistics: boolean;
  marketing: boolean;
  captured_at: string;
}

/** The attribution object auth accepts on the onboarding writes. */
export interface SignupAttribution {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  gclid?: string;
  gbraid?: string;
  wbraid?: string;
  landing_path?: string;
  referrer_host?: string;
  ga_client_id?: string;
  ga_session_id?: string;
  captured_at?: string;
  consent?: ConsentPayload;
}

export const DEFAULT_TRACK_TIMEOUT_MS = 1200;
export const DEFAULT_ATTRIBUTION_TIMEOUT_MS = 800;

// Auth accepts decisions up to 400 days old; Cookiebot asks again after 12 months.
const MAX_DECISION_AGE_MS = 399 * 24 * 60 * 60 * 1000;
const CONSENT_ID_RE = /^[A-Za-z0-9+/=_-]{1,128}$/;
const CLIENT_ID_RE = /^\d{1,20}\.\d{1,20}$/;
const SESSION_ID_RE = /^\d{1,20}$/;
const DISCARDED_PAID_PARAMS = ['gad_source', 'fbclid', 'msclkid'];

interface PageContext {
  page_location: string;
  page_path: string;
  page_referrer: string;
  page_title: string;
}

interface State {
  config: AnalyticsConfig | null;
  decision: ConsentDecision | null;
  eligibility: Eligibility;
  identity: Identity | null;
  // The identity last pushed to the dataLayer, so it can be brought in step
  // with `identity` whenever the gate reopens.
  publishedIdentity: Identity | null;
  touch: Touch | null;
  referrer: string;
  gtmLoaded: boolean;
  analyticsActive: boolean;
  hasPageView: boolean;
  lastPageLocation: string;
  pageContext: PageContext | null;
  listeners: Set<(decision: ConsentDecision) => void>;
  unsubscribe: (() => void) | null;
}

function initialState(): State {
  return {
    config: null,
    decision: null,
    eligibility: 'unknown',
    identity: null,
    publishedIdentity: null,
    touch: null,
    referrer: '',
    gtmLoaded: false,
    analyticsActive: false,
    hasPageView: false,
    lastPageLocation: '',
    pageContext: null,
    listeners: new Set(),
    unsubscribe: null,
  };
}

let state: State = initialState();

/**
 * Starts analytics for the page. Call once, synchronously, before the app
 * renders. A null config (not configured for this build) leaves everything off.
 */
export function bootAnalytics(config: AnalyticsConfig | null): void {
  if (state.config || !config) {
    return;
  }
  state.config = config;
  state.referrer = sanitizeReferrer(document.referrer, config.ignoredReferrerHosts);
  state.touch = captureTouch(
    window.location,
    document.referrer,
    config.ignoredReferrerHosts,
    new Date(),
  );

  gtag('consent', 'default', { ...DENIED_CONSENT, wait_for_update: 500 });
  gtag('set', 'ads_data_redaction', true);
  if (config.cookieDomain) {
    pushDataLayer({ cookie_domain: config.cookieDomain });
  }
  setKillSwitch(true);
  discardPaidParameters();

  if (config.devConsent) {
    const granted = config.devConsent === 'granted';
    applyDecision({
      hasResponse: true,
      statistics: granted,
      marketing: granted,
      decidedAt: new Date(),
    });
    return;
  }

  state.unsubscribe = onCookiebotEvents(() => applyDecision(readCookiebotDecision()));
  loadCookiebot(config.cookiebotCbid);
  applyDecision(readCookiebotDecision());
}

export function isAnalyticsEnabled(): boolean {
  return state.config !== null;
}

export function getConsentDecision(): ConsentDecision | null {
  return state.decision;
}

/** Subscribes to consent changes (e.g. to report them to the server). */
export function onConsentDecision(listener: (decision: ConsentDecision) => void): () => void {
  state.listeners.add(listener);
  return () => {
    state.listeners.delete(listener);
  };
}

function applyDecision(decision: ConsentDecision | null): void {
  if (!decision || sameDecision(decision, state.decision)) {
    return;
  }
  const hadMarketingConsent = marketingConsent();
  state.decision = decision;

  // Stop GA and clear its published identity before GTM processes a withdrawal.
  if (!analyticsGateOpen()) {
    setKillSwitch(true);
    state.analyticsActive = false;
    publishIdentity();
  }

  if (hadMarketingConsent !== marketingConsent()) {
    refreshConsentContext();
  }

  gtag('consent', 'update', googleConsentFor(decision));
  syncStoredTouch();
  evaluate();

  for (const listener of state.listeners) {
    listener(decision);
  }
}

// Persists the first touch only as far as consent allows, and removes it on
// withdrawal.
function syncStoredTouch(): void {
  const decision = state.decision;
  if (!decision?.hasResponse || !decision.statistics) {
    removeStoredTouch(window);
    return;
  }
  const merged = mergeTouch(readStoredTouch(window, new Date()), state.touch, decision.marketing);
  if (merged) {
    writeStoredTouch(window, merged);
  }
}

function gateOpen(): boolean {
  const config = state.config;
  if (!config || !allowsTags(state.decision)) {
    return false;
  }
  return !config.requireEligibility || state.eligibility === 'eligible';
}

function analyticsGateOpen(): boolean {
  return gateOpen() && state.decision?.statistics === true;
}

function marketingConsent(): boolean {
  return Boolean(state.decision?.hasResponse && state.decision.marketing);
}

// Discard unsupported paid parameters before the strict URL gate sees them.
// A failed replacement leaves the actual URL unsafe and cannot open the gate.
function discardPaidParameters(): void {
  const url = new URL(window.location.href);
  if (!DISCARDED_PAID_PARAMS.some((key) => url.searchParams.has(key))) {
    return;
  }

  for (const key of DISCARDED_PAID_PARAMS) {
    url.searchParams.delete(key);
  }

  try {
    window.history.replaceState(window.history.state, '', url.href);
  } catch {
    // The existing URL gate still rejects the discarded parameters.
  }
}

function refreshConsentContext(): void {
  const marketing = marketingConsent();
  const current = sanitizeUrl(window.location.href, marketing);
  const previous = state.lastPageLocation;

  // A permission change alone is not a navigation. Keep deduplication and the
  // next SPA referrer in step without emitting another page view.
  state.lastPageLocation = sanitizeUrl(previous, marketing);
  if (previous && sanitizeUrl(previous) === sanitizeUrl(current)) {
    state.lastPageLocation = current;
  }

  const context = state.pageContext;
  if (!context) {
    return;
  }

  state.pageContext = {
    ...context,
    page_location: sanitizeUrl(context.page_location, marketing),
    page_referrer: sanitizeUrl(context.page_referrer, marketing),
  };
  pushDataLayer({ ...state.pageContext });

  // Refresh the already activated tag too: automatic engagement can otherwise
  // retain the old URL even after dataLayer variables have been cleared.
  if (state.hasPageView && state.config) {
    gtag('config', state.config.measurementId, {
      ...state.pageContext,
      update: true,
      send_page_view: false,
    });
  }
}

// GA's documented opt-out: while true, the Google tag sends nothing for this
// measurement id, even if GTM is already loaded. configFromEnv refuses a build
// without a valid id, so the switch always exists once GTM can load.
function setKillSwitch(disabled: boolean): void {
  const measurementId = state.config?.measurementId;
  if (measurementId) {
    (window as unknown as Record<string, unknown>)[`ga-disable-${measurementId}`] = disabled;
  }
}

function evaluate(): void {
  const config = state.config;
  if (!config) {
    return;
  }
  const wasLoaded = state.gtmLoaded;
  const clean = isLocationClean(window.location);
  const open = analyticsGateOpen() && (wasLoaded || clean);
  const starting = open && !state.analyticsActive;

  setKillSwitch(!open);
  state.analyticsActive = open;

  if (!wasLoaded) {
    if (!gateOpen() || !clean) {
      return;
    }

    if (open) {
      publishContext();
      state.publishedIdentity = state.identity;
    }

    state.gtmLoaded = true;
    loadGtm(config.gtmId, config.gtmEnvQuery);
  } else {
    if (starting) {
      publishContext();
    }
    publishIdentity();
  }

  if (starting) {
    // Resume on the current page, without replaying navigation or actions from
    // the disabled interval. Duplicate Cookiebot events do not restart it.
    state.lastPageLocation = '';
    pushPageView({ firstHit: !state.hasPageView });
  }
}

function publishContext(): void {
  const location = sanitizeUrl(window.location.href, marketingConsent());

  state.pageContext = {
    page_location: state.hasPageView ? location : withLandingCampaign(location),
    page_path: sanitizePath(window.location.pathname),
    page_referrer: state.referrer,
    page_title: document.title,
  };

  pushDataLayer({
    ...state.pageContext,
    user_id: state.identity?.userId ?? null,
    org_ref: state.identity?.orgRef ?? null,
  });
}

// GA4 takes a session's source from its first hit. A visitor who answers the
// banner only after navigating away from the landing page (the SPA drops the
// query string) would otherwise start the session on a URL without the
// campaign, so it, and any Ads conversion credited to it, would read as
// direct. The first hit therefore carries this page load's landing campaign,
// exactly as it would have had the visitor answered on the landing page.
function withLandingCampaign(location: string): string {
  const touch = state.touch;
  if (!location || !touch || !hasCampaign(touch)) {
    return location;
  }

  const url = new URL(location);
  const keys = [...CAMPAIGN_KEYS, ...(marketingConsent() ? CLICK_KEYS : [])];
  if (keys.some((key) => url.searchParams.has(key))) {
    return location;
  }
  for (const key of keys) {
    const value = touch[key];
    if (value) {
      url.searchParams.set(key, value);
    }
  }
  return sanitizeUrl(url.toString(), marketingConsent());
}

function pushPageView(options: { firstHit?: boolean } = {}): void {
  if (!state.analyticsActive || !analyticsGateOpen()) {
    return;
  }

  const location = sanitizeUrl(window.location.href, marketingConsent());
  if (!location || location === state.lastPageLocation) {
    return;
  }
  const referrer = state.lastPageLocation
    ? sanitizeUrl(state.lastPageLocation, marketingConsent())
    : state.referrer;
  state.lastPageLocation = location;
  state.hasPageView = true;

  state.pageContext = {
    page_location: options.firstHit ? withLandingCampaign(location) : location,
    page_path: sanitizePath(window.location.pathname),
    page_title: document.title,
    page_referrer: referrer,
  };

  pushDataLayer({ event: 'virtual_page_view', ...state.pageContext });
}

function sameIdentity(a: Identity | null, b: Identity | null): boolean {
  return a?.userId === b?.userId && a?.orgRef === b?.orgRef;
}

// Brings the dataLayer's user in step with the current identity. Called only
// once GTM is loaded; the first load carries the identity itself.
function publishIdentity(): void {
  if (!state.gtmLoaded) {
    return;
  }

  const identity = state.analyticsActive ? state.identity : null;
  if (sameIdentity(identity, state.publishedIdentity)) {
    return;
  }
  state.publishedIdentity = identity;

  if (identity) {
    pushDataLayer({ event: 'user_identified', user_id: identity.userId, org_ref: identity.orgRef });
  } else {
    pushDataLayer({ event: 'user_reset', user_id: null, org_ref: null });
  }
}

/**
 * Call on every route change, and after a page scrubs a token or email from
 * the URL with history.replaceState (which emits no router event).
 */
export function notifyLocationChange(): void {
  if (!state.config) {
    return;
  }
  discardPaidParameters();
  evaluate();
  pushPageView();
}

export function setEligibility(eligibility: Eligibility): void {
  state.eligibility = eligibility;
  evaluate();
}

/**
 * Sets the signed-in user. While the gate is closed (eligibility not yet
 * known) it is held and published when the gate reopens.
 */
export function identify(identity: Identity): void {
  state.identity = identity;
  if (state.analyticsActive && analyticsGateOpen()) {
    publishIdentity();
  }
}

/** Logout or account switch: forget the user and close the gate until re-identified. */
export function resetIdentity(): void {
  state.identity = null;
  state.eligibility = 'unknown';
  evaluate();
}

/**
 * Sends one event. Resolves when GTM has processed it or after the timeout,
 * and immediately when the gate is closed, so callers can await it before a
 * full-page navigation without risking a hang.
 */
export function track<E extends AnalyticsEventName>(
  name: E,
  params: AnalyticsEvents[E],
  options: { timeoutMs?: number } = {},
): Promise<void> {
  if (!isTrackingActive()) {
    return Promise.resolve();
  }
  const timeoutMs = options.timeoutMs ?? DEFAULT_TRACK_TIMEOUT_MS;

  return new Promise((resolve) => {
    let settled = false;
    const settle = (): void => {
      if (!settled) {
        settled = true;
        resolve();
      }
    };
    const timer = window.setTimeout(settle, timeoutMs);

    pushDataLayer({
      event: name,
      ...params,
      eventCallback: () => {
        window.clearTimeout(timer);
        settle();
      },
      eventTimeout: timeoutMs,
    });
  });
}

/** Whether GA4 events may be sent right now (independent of marketing tags). */
export function isTrackingActive(): boolean {
  return state.analyticsActive && analyticsGateOpen();
}

/**
 * Sends an event and then leaves the page. With tracking off the navigation
 * happens synchronously; otherwise it waits for delivery or the timeout.
 */
export function trackBeforeNavigation<E extends AnalyticsEventName>(
  name: E,
  params: AnalyticsEvents[E],
  navigate: () => void,
): void {
  if (!isTrackingActive()) {
    navigate();
    return;
  }
  void track(name, params).then(navigate);
}

/**
 * The decision in auth's shape. The decision time is when the visitor chose
 * (so decisions from different browsers order correctly), falling back to now
 * when Cookiebot does not record it or it is outside what auth accepts.
 */
export function consentPayloadFor(
  decision: ConsentDecision,
  now: Date = new Date(),
): ConsentPayload {
  const at = decision.decidedAt;
  const capturedAt =
    !at ||
    !Number.isFinite(at.getTime()) ||
    at.getTime() > now.getTime() ||
    now.getTime() - at.getTime() > MAX_DECISION_AGE_MS
      ? now
      : at;

  return {
    source: 'cookiebot',
    statistics: decision.hasResponse && decision.statistics,
    marketing: decision.hasResponse && decision.marketing,
    captured_at: capturedAt.toISOString(),
    ...(decision.consentId && CONSENT_ID_RE.test(decision.consentId)
      ? { consent_id: decision.consentId }
      : {}),
  };
}

function gtagGet(
  measurementId: string,
  field: 'client_id' | 'session_id',
  timeoutMs: number,
): Promise<string | undefined> {
  return new Promise((resolve) => {
    const timer = window.setTimeout(() => resolve(undefined), timeoutMs);
    gtag('get', measurementId, field, (value: unknown) => {
      window.clearTimeout(timer);
      resolve(typeof value === 'string' || typeof value === 'number' ? String(value) : undefined);
    });
  });
}

async function readGaIds(
  config: AnalyticsConfig,
  timeoutMs: number,
): Promise<{ clientId: string | undefined; sessionId: string | undefined }> {
  if (!state.gtmLoaded) {
    return {
      clientId: readClientId(document.cookie, config.cookieDomain),
      sessionId: undefined,
    };
  }

  // Session cookie formats are not an API. Ask the configured Google tag;
  // only the domain-filtered client ID has a first-party cookie fallback.
  const [clientId, sessionId] = await Promise.all([
    gtagGet(config.measurementId, 'client_id', timeoutMs),
    gtagGet(config.measurementId, 'session_id', timeoutMs),
  ]);
  return {
    clientId:
      readClientId(document.cookie, config.cookieDomain) ??
      (clientId && CLIENT_ID_RE.test(clientId) ? clientId : undefined),
    sessionId: sessionId && SESSION_ID_RE.test(sessionId) ? sessionId : undefined,
  };
}

/**
 * The attribution to send with an onboarding write, or undefined when
 * analytics is off or the visitor has not answered the banner. Campaign values
 * and GA ids need statistics consent; ad-click ids also need marketing.
 */
export async function getSignupAttribution(
  options: { timeoutMs?: number } = {},
): Promise<SignupAttribution | undefined> {
  const config = state.config;
  if (!config || !state.decision?.hasResponse) {
    return undefined;
  }

  // Reading the GA ids can wait on the Google tag, and the visitor can change
  // their choice meanwhile. The server stores this consent as permission to
  // send, so everything below follows the decision as it stands afterwards.
  const ids = state.decision.statistics
    ? await readGaIds(config, options.timeoutMs ?? DEFAULT_ATTRIBUTION_TIMEOUT_MS)
    : undefined;

  const decision = state.decision;
  if (state.config !== config || !decision?.hasResponse) {
    return undefined;
  }
  const now = new Date();

  const attribution: SignupAttribution = { consent: consentPayloadFor(decision, now) };
  if (!decision.statistics) {
    return attribution;
  }

  const touch = mergeTouch(readStoredTouch(window, now), state.touch, decision.marketing);
  if (touch) {
    for (const key of [...CAMPAIGN_KEYS, ...CLICK_KEYS, 'landing_path', 'referrer_host'] as const) {
      const value = touch[key];
      if (value) {
        attribution[key] = value;
      }
    }
    attribution.captured_at = touch.captured_at;
  }

  if (ids?.clientId) {
    attribution.ga_client_id = ids.clientId;
  }
  if (ids?.sessionId) {
    attribution.ga_session_id = ids.sessionId;
  }
  return attribution;
}

/** Test-only: drops all runtime state so each test boots afresh. */
export function resetAnalyticsForTests(): void {
  state.unsubscribe?.();
  state = initialState();
}
